import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { DomainRegistrar, DomainRegistrarFormFields, DomainRegistrarsFormInputs, DomainRegistrarsQueryKeys, useCreateDomainRegistrarsMutation } from '@/modules/DomainRegistrars';

import { DomainRegistrarsFormView } from './DomainRegistrarsFormView';
import { domainRegistrarsFormValidationSchema } from './validation';

export const DomainRegistrarsCreateFormContainer: FC = memo(() => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<DomainRegistrarFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DomainRegistrarsFormInputs>>(null);

  const { mutate: createDomainRegistrarMutation, isLoading: createDomainRegistrarsIsLoading } =
    useCreateDomainRegistrarsMutation()({
      onSuccess: async (data: DomainRegistrar) => {
        appMessageApi.success('Реєстратора домену успішно створено!');
        navigate(AppRoutes.DomainRegistrarsById.replace(':domainRegistrarId', data.id));
        await queryClient.refetchQueries([DomainRegistrarsQueryKeys.GetAll]);
      },
      onError: (err) => {
        setFormErrors(extractFormErrors<typeof DomainRegistrarsFormInputs>(err));
        messageOnError(err, appMessageApi);
      }
    });

  const initialValues: DomainRegistrarFormFields = {
    name: undefined,
    link: undefined
  };

  const handleFinish = useCallback((values: DomainRegistrarFormFields): void => {
    setFormErrors(null);
    createDomainRegistrarMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.DomainRegistrars);
  }, []);

  return (
    <DomainRegistrarsFormView
      formName="domain-registrar-create"
      formType="create"
      form={ form }
      formErrors={ formErrors }
      initialValues={ initialValues }
      isFormLoading={ createDomainRegistrarsIsLoading }
      validationSchema={ domainRegistrarsFormValidationSchema }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
